"use client";

import { useRef, useState, useSyncExternalStore, type FormEvent, type InputHTMLAttributes, type TextareaHTMLAttributes } from "react";
import { flushSync } from "react-dom";
import type { Locale } from "@/kernel/locale";
import { formatMessage } from "@/platform/i18n/ui/client";
import { useLoopVideoBox } from "@/shared/loop-video";
import { Icon, type IconName } from "@/shared/ui";
import { LEAD_INTERESTS, LEAD_LIMITS, type LeadFieldError, type LeadInterest, type LeadRequiredField, type LeadSubmission, type LeadTextField } from "../../contracts";
import { leadFieldError } from "../../domain/submission";
import { PORTHOLE_VIDEO } from "../render/media";
import { submitLead } from "./gateway";

export type ConnectFormCopy = {
  title: string;
  subtitle: string;
  optional: string;
  labels: Record<"company" | "industry" | "name" | "role" | "contact" | "branches" | "interests" | "note", string>;
  placeholders: Record<"industry" | "note", string>;
  interests: Record<LeadInterest, { label: string; inline: string }>;
  submit: string;
  submitting: string;
  privacy: string;
  /** Label of the hidden honeypot field, read by no person. */
  honeypot: string;
  errors: Record<LeadFieldError, string>;
  failure: {
    rateLimited: { title: string; text: string };
    unavailable: { title: string; text: string };
    /** Template with {reference}. */
    reference: string;
  };
  /** Templates with {name}, {company}, {contact} and {interests}. */
  done: { title: string; text: string; textWithInterests: string; again: string };
};

const INTEREST_ICONS: Partial<Record<LeadInterest, IconName>> = {
  webChat: "globe",
  messengers: "msg",
  tablets: "tablet",
  videoOperator: "video",
};

const REQUIRED: readonly LeadRequiredField[] = ["company", "name", "contact"];

const subscribeToNothing = () => () => {};

type Outcome = { title: string; text: string };

type Failure = { kind: "rateLimited" | "unavailable"; reference?: string };

function readSubmission(form: HTMLFormElement, locale: Locale): LeadSubmission {
  const data = new FormData(form);
  const text = (name: string) => {
    const value = data.get(name);
    return typeof value === "string" ? value.trim() : "";
  };
  const picked = new Set(data.getAll("interest"));
  return {
    company: text("org"),
    sphere: text("field"),
    name: text("name"),
    role: text("role"),
    contact: text("contact"),
    pointsCount: text("branches"),
    interests: LEAD_INTERESTS.filter((interest) => picked.has(interest)),
    comment: text("note"),
    locale,
    website: text("website"),
  };
}

/** The connect form card (prototype "Обговорити підключення") with its nodding porthole loop. */
export function ConnectForm({ copy, locale }: { copy: ConnectFormCopy; locale: Locale }) {
  const porthole = useRef<HTMLDivElement>(null);
  const form = useRef<HTMLFormElement>(null);
  const done = useRef<HTMLDivElement>(null);
  const fields = useRef<Partial<Record<LeadRequiredField, HTMLInputElement | null>>>({});
  const [errors, setErrors] = useState<Partial<Record<LeadTextField, LeadFieldError | null>>>({});
  const [outcome, setOutcome] = useState<Outcome | null>(null);
  const [sending, setSending] = useState(false);
  const [failure, setFailure] = useState<Failure | null>(null);
  const alert = useRef<HTMLDivElement>(null);
  // False in the server's HTML and until hydration: a form sent before its
  // handler exists would put the visitor's details in the page address.
  const interactive = useSyncExternalStore(subscribeToNothing, () => true, () => false);
  useLoopVideoBox(porthole);

  const validate = (field: LeadRequiredField): boolean => {
    const error = leadFieldError(field, fields.current[field]?.value ?? "");
    setErrors((current) => ({ ...current, [field]: error }));
    return error === null;
  };

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (sending) return;
    const element = event.currentTarget;
    setFailure(null);
    const bad = REQUIRED.filter((field) => !validate(field));
    if (bad.length) {
      fields.current[bad[0]]?.focus();
      return;
    }
    const submission = readSubmission(element, locale);
    setSending(true);
    const result = await submitLead(submission);
    setSending(false);
    if (!result.ok) {
      if (result.code === "invalid") {
        flushSync(() => setErrors(result.fields));
        const first = REQUIRED.find((field) => result.fields[field]);
        if (first) fields.current[first]?.focus();
        else element.querySelector<HTMLElement>("[aria-invalid='true']")?.focus();
        return;
      }
      // The details stay in the form; the visitor may simply send again.
      flushSync(() => setFailure(result.code === "rate_limited" ? { kind: "rateLimited" } : { kind: "unavailable", reference: result.reference }));
      alert.current?.focus({ preventScroll: true });
      alert.current?.scrollIntoView({ block: "nearest", behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth" });
      return;
    }
    const interests = new Intl.ListFormat(locale, { type: "conjunction" }).format(submission.interests.map((interest) => copy.interests[interest].inline));
    const params = { name: submission.name, company: submission.company, contact: submission.contact, interests };
    flushSync(() => setOutcome({
      title: formatMessage(copy.done.title, params, locale),
      text: formatMessage(submission.interests.length ? copy.done.textWithInterests : copy.done.text, params, locale),
    }));
    done.current?.focus({ preventScroll: true });
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    done.current?.scrollIntoView({ block: "nearest", behavior: reduce ? "auto" : "smooth" });
  };

  const again = () => {
    form.current?.reset();
    flushSync(() => {
      setErrors({});
      setFailure(null);
      setOutcome(null);
    });
    fields.current.company?.focus();
  };

  // The server's message for an optional field (only a length it measures
  // after normalizing); editing the field clears it.
  const optionalError = (field: Exclude<LeadTextField, LeadRequiredField>, errorId: string) => ({
    "aria-describedby": errors[field] ? errorId : undefined,
    "aria-invalid": errors[field] ? true : undefined,
    onInput: () => { if (errors[field]) setErrors((current) => ({ ...current, [field]: null })); },
  }) satisfies InputHTMLAttributes<HTMLInputElement> & TextareaHTMLAttributes<HTMLTextAreaElement>;

  const optionalMessage = (field: Exclude<LeadTextField, LeadRequiredField>, errorId: string) => {
    const error = errors[field];
    return <span className="err" id={errorId} aria-live="polite">{error ? copy.errors[error] : ""}</span>;
  };

  const required = (field: LeadRequiredField, id: string, errorId: string, label: string, input: InputHTMLAttributes<HTMLInputElement>) => {
    const error = errors[field];
    return (
      <div className="f">
        <label htmlFor={id}>{label}</label>
        <input
          id={id}
          ref={(element) => { fields.current[field] = element; }}
          type="text"
          required
          aria-describedby={errorId}
          aria-invalid={error === undefined ? undefined : error !== null}
          maxLength={LEAD_LIMITS[field]}
          onInput={() => { if (error) validate(field); }}
          {...input}
        />
        <span className="err" id={errorId} aria-live="polite">{error ? copy.errors[error] : ""}</span>
      </div>
    );
  };

  return (
    <div className="form-card" id="connect">
      <div className="fc-head">
        <div className="porthole" ref={porthole} aria-hidden="true">
          <video className="square" muted playsInline loop preload="none" poster={PORTHOLE_VIDEO.poster}>
            <source src={PORTHOLE_VIDEO.webm} type="video/webm" />
            <source src={PORTHOLE_VIDEO.mp4} type="video/mp4" />
          </video>
        </div>
        <div><h3>{copy.title}</h3><p>{copy.subtitle}</p></div>
      </div>

      <form className="form" id="connectForm" ref={form} method="post" noValidate hidden={outcome !== null} onSubmit={onSubmit}>
        {required("company", "f-org", "e-org", copy.labels.company, { name: "org", autoComplete: "organization" })}
        <div className="f">
          <label htmlFor="f-field">{copy.labels.industry} <em>{copy.optional}</em></label>
          <input id="f-field" name="field" type="text" autoComplete="off" maxLength={LEAD_LIMITS.sphere} placeholder={copy.placeholders.industry} {...optionalError("sphere", "e-field")} />
          {optionalMessage("sphere", "e-field")}
        </div>
        {required("name", "f-name", "e-name", copy.labels.name, { name: "name", autoComplete: "name" })}
        <div className="f">
          <label htmlFor="f-role">{copy.labels.role} <em>{copy.optional}</em></label>
          <input id="f-role" name="role" type="text" autoComplete="organization-title" maxLength={LEAD_LIMITS.role} {...optionalError("role", "e-role")} />
          {optionalMessage("role", "e-role")}
        </div>
        {required("contact", "f-contact", "e-contact", copy.labels.contact, { name: "contact", inputMode: "text", autoComplete: "off", autoCapitalize: "off", spellCheck: "false" })}
        <div className="f">
          <label htmlFor="f-branches">{copy.labels.branches} <em>{copy.optional}</em></label>
          <input id="f-branches" name="branches" type="text" inputMode="numeric" autoComplete="off" maxLength={LEAD_LIMITS.pointsCount} {...optionalError("pointsCount", "e-branches")} />
          {optionalMessage("pointsCount", "e-branches")}
        </div>
        <fieldset className="f wide">
          <legend>{copy.labels.interests}</legend>
          <div className="pick">
            {LEAD_INTERESTS.map((interest) => {
              const icon = INTEREST_ICONS[interest];
              return (
                <label key={interest}>
                  <input type="checkbox" name="interest" value={interest} />
                  <span>{icon ? <Icon name={icon} className="ic" /> : null}{copy.interests[interest].label}</span>
                </label>
              );
            })}
          </div>
        </fieldset>
        <div className="f wide">
          <label htmlFor="f-note">{copy.labels.note} <em>{copy.optional}</em></label>
          <textarea id="f-note" name="note" rows={3} maxLength={LEAD_LIMITS.comment} placeholder={copy.placeholders.note} {...optionalError("comment", "e-note")} />
          {optionalMessage("comment", "e-note")}
        </div>
        <div className="hp" aria-hidden="true">
          <label htmlFor="f-website">{copy.honeypot}</label>
          <input id="f-website" name="website" type="text" tabIndex={-1} autoComplete="off" />
        </div>
        <div className="form-alert" id="formAlert" ref={alert} role="alert" tabIndex={-1} hidden={failure === null}>
          {failure ? (
            <>
              <Icon name="alert" className="ic" />
              <div>
                <p className="form-alert-title">{copy.failure[failure.kind].title}</p>
                <p>{copy.failure[failure.kind].text}</p>
                {failure.reference ? <p className="form-alert-ref">{formatMessage(copy.failure.reference, { reference: failure.reference }, locale)}</p> : null}
              </div>
            </>
          ) : null}
        </div>
        <div className="f-foot">
          <button className="btn btn-primary" type="submit" aria-busy={sending} disabled={sending || !interactive}>
            <span className="spin" aria-hidden="true" />
            <span className="lbl">{sending ? copy.submitting : copy.submit}</span>
            {sending ? null : <Icon name="arrow" className="ic" />}
          </button>
          <p>{copy.privacy}</p>
        </div>
      </form>

      <div className="done" id="formDone" ref={done} role="status" hidden={outcome === null} tabIndex={-1}>
        <span className="done-ic" aria-hidden="true"><Icon name="check" className="ic" /></span>
        <h4 id="doneTitle">{outcome?.title}</h4>
        <p id="doneText">{outcome?.text}</p>
        <button className="btn btn-ghost btn-sm" type="button" id="formAgain" onClick={again}>{copy.done.again}</button>
      </div>
    </div>
  );
}
